import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  makeStyles,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { useHistory, useLocation } from "react-router";
import { useRecoilState, useRecoilValueLoadable } from "recoil";

import { CustomButton } from "@/components/Button";
import { Chevron } from "@/components/Chevron";
import { CustomSelector } from "@/components/Selecter";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";
import { BalanceSelectedStartingMonth, BalanceSelectedEndingMonth, SelectedPropertyIds } from "@/view_models/atoms";
import { OwnedPropertiesWithSample } from "@/view_models/propertySelectors";

import { BalancePeriodFilter, BalancePropertyFilter } from "./components/BalanceFilters";
import { BalanceOverview } from "./components/BalanceOverview";
import { BalanceError, BalanceLoading } from "./components/BalanceState";
import { MonthlyCashflowCards } from "./components/MonthlyCashflowCards";
import { TaxReturnCards } from "./components/TaxReturnCards";
import { useBalanceSheetData } from "./hooks/useBalanceSheet";
import { useBalanceSummary } from "./hooks/useBalanceSummary";
import { useProfitAndLossSheetData } from "./hooks/useProfitAndLoss";
import { balancePeriod, BalancePreset } from "./utils/balanceSummary";

const useStyles = makeStyles({
  page: {
    padding: "24px 24px 32px",
    display: "flex",
    flexDirection: "column",
    gap: layout.space[24],
    [theme.breakpoints.up("md")]: { padding: "32px 0 48px", gap: layout.space[24] },
  },
  heading: { ...textStyles["PC/Heading/H2"] },
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: layout.space[16],
    minWidth: 0,
    [theme.breakpoints.up("md")]: { alignItems: "flex-end" },
  },
  titleGroup: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: layout.space[12] },
  back: {
    display: "flex",
    alignItems: "center",
    gap: layout.space[4],
    color: colors["text/link"],
    ...textStyles["PC/Body/Small"],
    whiteSpace: "nowrap",
  },
  filters: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: layout.space[12], minWidth: 0, maxWidth: "100%" },
  taxYear: { width: 180 },
  note: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  detail: {
    background: colors["surface/card"],
    borderRadius: layout.radius.lg,
    boxShadow: "none",
    "&:before": { display: "none" },
    "&.Mui-expanded": { margin: 0 },
    "& .MuiAccordionSummary-root": { padding: "0 24px", minHeight: 56 },
    "& .MuiAccordionDetails-root": { display: "block", padding: "0 24px 24px", minWidth: 0 },
    "& .MuiAccordionSummary-content": { minWidth: 0 },
  },
  detailTitle: { ...textStyles["SP/Heading/H4"], [theme.breakpoints.up("md")]: textStyles["PC/Heading/H4"] },
  empty: {
    padding: layout.space[32],
    background: colors["surface/card"],
    borderRadius: layout.radius.lg,
    display: "flex",
    flexDirection: "column",
    gap: layout.space[24],
    alignItems: "center",
    textAlign: "center",
  },
});

const CashflowDetails: React.FC<{ start: Dayjs }> = ({ start }) => {
  const { data, isLoading, error, monthlyResults } = useBalanceSheetData();
  if (isLoading) return <BalanceLoading />;
  if (error) return <BalanceError />;
  return <MonthlyCashflowCards rows={data} monthlyResults={monthlyResults ?? []} selectedStartingMonth={start} />;
};
const TaxContent = () => {
  const { isLoading, error, monthlyResults } = useProfitAndLossSheetData();
  if (isLoading) return <BalanceLoading />;
  if (error) return <BalanceError />;
  return <TaxReturnCards monthlyResults={monthlyResults ?? []} />;
};
const OverviewContent: React.FC<{ start: Dayjs; onSelectProperty: (id: number) => void }> = ({
  start,
  onSelectProperty,
}) => {
  const classes = useStyles();
  const { loading, error, data } = useBalanceSummary();
  const [detailOpen, setDetailOpen] = React.useState(false);
  if (loading) return <BalanceLoading />;
  if (error || !data || !data.available) return <BalanceError />;
  return (
    <>
      <BalanceOverview data={data} onSelect={onSelectProperty} />
      {data.includesSample && <Typography className={classes.note}>サンプル物件を含む収支です。</Typography>}
      <Accordion className={classes.detail} expanded={detailOpen} onChange={(_, expanded) => setDetailOpen(expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.detailTitle}>詳しい収支</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {detailOpen && (
            <>
              <Typography className={classes.note} style={{ marginBottom: 16 }}>
                税金・保険・購入や売却などの一時的な収支を含む、月ごとの詳しい内訳です。
              </Typography>
              <CashflowDetails start={start} />
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export const BalancePage: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<{ initialMode?: "tax_return" }>();
  const taxMode = location.state?.initialMode === "tax_return";
  const { isMobile } = useDevice();
  const properties = useRecoilValueLoadable(OwnedPropertiesWithSample);
  const [selectedIds, setSelectedIds] = useRecoilState(SelectedPropertyIds);
  const [start, setStart] = useRecoilState(BalanceSelectedStartingMonth);
  const [end, setEnd] = useRecoilState(BalanceSelectedEndingMonth);
  const [preset, setPreset] = React.useState<BalancePreset>("month");
  const [taxYear, setTaxYear] = React.useState(dayjs().year());
  const [initialized, setInitialized] = React.useState(false);
  React.useEffect(() => {
    const period = balancePeriod(taxMode ? "year" : "month");
    setStart(period.start);
    setEnd(period.end);
    setTaxYear(dayjs().year());
    setPreset("month");
    setInitialized(true);
  }, [taxMode, setStart, setEnd]);
  const safeStart = dayjs.isDayjs(start) ? start : dayjs(start as never);
  const safeEnd = dayjs.isDayjs(end) ? end : dayjs(end as never);
  const changePeriod = (next: BalancePreset, customStart?: Dayjs, customEnd?: Dayjs) => {
    if (next === "custom") {
      if (!customStart || !customEnd) return;
      setStart(customStart);
      setEnd(customEnd);
    } else {
      const period = balancePeriod(next);
      setStart(period.start);
      setEnd(period.end);
    }
    setPreset(next);
  };
  const options =
    properties.state === "hasValue"
      ? properties.contents.map((holder) => ({ id: holder.id, label: holder.property.name }))
      : [];
  const chosenIds = selectedIds.filter((id) => options.some((option) => option.id === id));
  const title = taxMode ? "確定申告用表示" : "収支";
  return (
    <BaseTemplate
      headerTitle={isMobile ? title : undefined}
      onBack={isMobile ? () => history.goBack() : undefined}
      isDense
      isPcV2
      disableBottomPadding
    >
      <Box className={classes.page}>
        <Box className={classes.titleRow}>
          {!isMobile && (
            <Box className={classes.titleGroup}>
              <ButtonBase className={classes.back} onClick={() => history.push("/mypage")}>
                <Chevron direction="back" />
                ホーム
              </ButtonBase>
              <Typography component="h1" className={classes.heading}>
                {title}
              </Typography>
            </Box>
          )}
          <Box className={classes.filters}>
            <BalancePropertyFilter options={options} selectedIds={chosenIds} onChange={setSelectedIds} />
            {taxMode ? (
              <Box className={classes.taxYear}>
                <CustomSelector
                  label="対象年"
                  value={String(taxYear)}
                  options={Array.from({ length: 10 }, (_, index) => ({
                    value: String(dayjs().year() - index),
                    label: `${dayjs().year() - index}年`,
                  }))}
                  onChange={(event) => {
                    const year = Number(event.target.value);
                    setTaxYear(year);
                    setStart(dayjs().year(year).startOf("year"));
                    setEnd(dayjs().year(year).endOf("year"));
                  }}
                />
              </Box>
            ) : (
              initialized && (
                <BalancePeriodFilter preset={preset} start={safeStart} end={safeEnd} onChange={changePeriod} />
              )
            )}
          </Box>
        </Box>
        {!taxMode && preset === "year" && <Typography className={classes.note}>今年は1月〜12月の計算結果を表示します（将来の月を含みます）。</Typography>}
        {!initialized || properties.state === "loading" ? (
          <BalanceLoading />
        ) : properties.state === "hasError" ? (
          <BalanceError />
        ) : options.length === 0 ? (
          <Box className={classes.empty}>
            <Typography component="h2" className={classes.detailTitle}>
              物件を登録すると収支を確認できます
            </Typography>
            <CustomButton onClick={() => history.push("/management/own")}>保有物件を開く</CustomButton>
          </Box>
        ) : chosenIds.length === 0 ? (
          <Box className={classes.empty}>
            <Typography component="h2" className={classes.detailTitle}>
              表示する物件を選択してください
            </Typography>
            <Typography className={classes.note}>上の「物件を選択」から確認したい物件を選べます。</Typography>
          </Box>
        ) : taxMode ? (
          <TaxContent />
        ) : (
          <OverviewContent start={safeStart} onSelectProperty={(id) => setSelectedIds([id])} />
        )}
        <Typography className={classes.note}>
          ※こちらの機能はユーザー様が入力された情報を基に作成されるものであり、当社がその正確性を保証するものではありません。
          {taxMode && (
            <>
              <br />
              <br />
              ※本画面は一般的な申告例です。最終判断は税理士等にご確認ください
            </>
          )}
        </Typography>
      </Box>
    </BaseTemplate>
  );
};
